'use client'

import dynamic from 'next/dynamic'

import CloseIcon from '@mui/icons-material/Close'
import MenuIcon from '@mui/icons-material/Menu'
import { Backdrop, Box, Button, Grid, IconButton } from '@mui/material'
import { useSession } from 'next-auth/react'
import NextTopLoader from 'nextjs-toploader'

import { AnnouncementBarContainer } from '@/components/AnnouncementBarContainer'
import { ContextBar } from '@/components/ContextBar'
import { Footer } from '@/components/Footer/Footer'
import { Header } from '@/components/Header/Header'
import { SearchBar } from '@/components/Header/SearchBar'
import { LogoMainPageLink } from '@/components/LogoMainPageLink'
import { MainBar } from '@/components/MainBar'
import { MenuItemsBar } from '@/components/MenuItemsBar'
import { WelcomeDialogue } from '@/components/WelcomeDialogue/WelcomeDialogue'
import { getAnnouncementBarFromSanityLayout } from '@/helpers/getAnnouncementBarFromSanityLayout'
import { useGlobalErrorTracking } from '@/hooks/useGlobalErrorTracking'
import { useHeaderContext } from '@/hooks/useHeaderContext'
import { useIsLoggedIn } from '@/hooks/useIsLoggedIn'
import { useMedia } from '@/hooks/useMedia'
import { useNavigationTree } from '@/hooks/useNavigationTree'
import { usePageViewTracking } from '@/hooks/usePageViewTracking'
import { useQuickSearchReset } from '@/hooks/useQuickSearchReset'
import { useSiteContext } from '@/hooks/useSiteContext'
import { useTenant } from '@/hooks/useTenant'
import { useTranslations } from '@/hooks/useTranslations'

import type * as React from 'react'
import type { CustomPageProps } from '@/interfaces/CustomPage'

const QuickSearchResults = dynamic(
  () => import('@/components/QuickSearchResults').then((mod) => mod.QuickSearchResults),
  { ssr: false }
)

const SidebarMenu = dynamic(() => import('@/components/SidebarMenu/SidebarMenu').then((mod) => mod.SidebarMenu), {
  ssr: false,
})

//**Dynamically loading the Main bar actions due to useCart for the totalItems */
const MainBarActions = dynamic(
  () => import('@/components/MainBarActions/MainBarActions').then((mod) => mod.MainBarActions),
  {
    ssr: false,
  }
)

const getSearchingSx = (isSearchOpen: boolean) => ({
  visibility: isSearchOpen ? 'hidden' : 'visible',
  opacity: isSearchOpen ? 0 : 1,
  maxHeight: isSearchOpen ? '0px' : '190px',
  transition: 'visibility .1s,  max-height 0.3s',
})

interface LayoutMainBarProps {
  isSearchOpen: boolean
  isMobile: boolean
  sxSearching: ReturnType<typeof getSearchingSx>
  toggleSideMenuOpen: ReturnType<typeof useHeaderContext>['toggleSideMenuOpen']
  translate: ReturnType<typeof useTranslations>['translate']
  onCloseSearch: VoidFunction
}

const LayoutMainBar: React.FC<LayoutMainBarProps> = ({
  isSearchOpen,
  isMobile,
  sxSearching,
  toggleSideMenuOpen,
  translate,
  onCloseSearch,
}) => (
  <MainBar>
    <Grid
      item
      xs={isSearchOpen ? 6 : 'auto'}
      className="flex"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        height: {
          xs: 'auto',
          md: '48px',
        },
        gap: {
          xs: 0,
          md: 2,
        },
        ' &.flex': {
          flex: 1,
        },
      }}
    >
      <IconButton
        data-testid="layout-menuButton"
        onClick={toggleSideMenuOpen(true)}
        sx={{
          display: {
            xs: 'flex',
            md: 'none',
          },
          ...sxSearching,
        }}
      >
        <MenuIcon color="secondary" />
      </IconButton>
      <LogoMainPageLink
        logoSize={isMobile ? { width: 93, height: 33 } : undefined}
        logoAlt={translate('layout.logo')?.toString()}
      />
      <Box sx={{ display: { xs: 'none', md: 'block' }, width: '100%' }}>
        <SearchBar variant="desktop" />
      </Box>
    </Grid>
    <Grid item xs={isSearchOpen ? 6 : 'auto'} display="flex" justifyContent="flex-end">
      <Box sx={sxSearching}>
        <MainBarActions />
      </Box>
      {isSearchOpen && !isMobile && (
        <Button disableRipple onClick={onCloseSearch} sx={{ paddingRight: { lg: 0 } }} startIcon={<CloseIcon />}>
          {translate('common.close')}
        </Button>
      )}
    </Grid>
  </MainBar>
)

export const Layout: React.FC<CustomPageProps> = ({ siteSettings: settings, children }) => {
  const { isSet } = useSiteContext()
  const { data: session } = useSession()
  const { toggleSideMenuOpen, isSearchOpen, setIsSearchOpen, isSideMenuOpen } = useHeaderContext()
  const { isDesktop, isMobile } = useMedia()
  const { translate, language } = useTranslations()
  const { reset } = useQuickSearchReset()
  const { features } = useTenant()

  usePageViewTracking()
  useGlobalErrorTracking()

  const header = settings?.layout.headers.find((header) => header.language === language)
  const footer = settings?.layout.footers.find((footer) => footer.language === language)

  const usps = header?.uspBanner
  const navigation = useNavigationTree(header?.menuItems)
  const announcementBar = header ? getAnnouncementBarFromSanityLayout(header, session) : null
  const isLoggedIn = useIsLoggedIn()
  const SiteNotSet = !isLoggedIn && !isSet && features?.b2b && features.b2c
  const sxSearching = getSearchingSx(isSearchOpen)

  const handleCloseSearch = () => {
    setIsSearchOpen(false)
    reset()
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Header navigation={navigation}>
        <Box sx={sxSearching}>
          <ContextBar usps={usps} />
        </Box>
        {!(isMobile && isSearchOpen) && (
          <LayoutMainBar
            isSearchOpen={isSearchOpen}
            isMobile={isMobile}
            sxSearching={sxSearching}
            toggleSideMenuOpen={toggleSideMenuOpen}
            translate={translate}
            onCloseSearch={handleCloseSearch}
          />
        )}
        {!isSearchOpen && <MenuItemsBar navigation={navigation} />}
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          <SearchBar variant="mobile" />
        </Box>
        {isSearchOpen && <QuickSearchResults />}
        {isSideMenuOpen && <SidebarMenu navigation={navigation} />}
      </Header>
      <NextTopLoader showSpinner={isDesktop} />
      {announcementBar != null && <AnnouncementBarContainer announcementBar={announcementBar} />}
      {/* Preserve menu spacing         */}
      {isSearchOpen && <Box sx={{ height: (theme) => theme.customDimensions.searchHeaderOffset }}></Box>}
      {children}

      <Footer footer={footer} />
      <WelcomeDialogue openWelcome={SiteNotSet} />
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.appBar - 1 }}
        open={isSearchOpen}
        onClick={handleCloseSearch}
      />
    </div>
  )
}
